import React, { useEffect, useState } from 'react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Select } from './ui/Select';
import { Plus, X, Save, RotateCcw, AlertCircle, CheckCircle, ArrowLeft, Info } from 'lucide-react';
interface CreditScore {
  id: string;
  name: string;
  description: string;
  totalScoreRange: number;
  createdAt: string;
  status: 'active' | 'inactive';
}
interface Rule {
  id: string;
  factor: string;
  operator: string;
  value: string;
  value2?: string;
}
interface Condition {
  id: string;
  rules: Rule[];
  points: number;
  ruleOperator: 'AND' | 'OR';
}
interface ScoringGroup {
  id: string;
  groupName: string;
  weightage: number;
  totalPoints: number;
  conditions: Condition[];
}
interface CreditScoringFormProps {
  creditScore: CreditScore | null;
  onBack: () => void;
  onSave: () => void;
}
export function CreditScoringForm({
  creditScore,
  onBack,
  onSave
}: CreditScoringFormProps) {
  // Header fields
  const [scoreId, setScoreId] = useState(creditScore?.id || `CS-${Date.now()}`);
  const [scoreName, setScoreName] = useState(creditScore?.name || '');
  const [scoreDescription, setScoreDescription] = useState(creditScore?.description || '');
  const [totalScoreRange, setTotalScoreRange] = useState(creditScore?.totalScoreRange || 100);
  // Scoring groups
  const [scoringGroups, setScoringGroups] = useState<ScoringGroup[]>([]);
  const [totalWeightage, setTotalWeightage] = useState(0);
  const [showTooltip, setShowTooltip] = useState<string | null>(null);
  const scoringFactorOptions = [{
    value: '',
    label: 'Select scoring factor...'
  }, {
    value: 'age',
    label: 'Age'
  }, {
    value: 'region',
    label: 'Region'
  }, {
    value: 'city',
    label: 'City'
  }, {
    value: 'monthly_sales',
    label: 'Monthly Sales'
  }, {
    value: 'business_tenure',
    label: 'Business Tenure'
  }, {
    value: 'credit_history',
    label: 'Credit History'
  }, {
    value: 'income',
    label: 'Monthly Income'
  }, {
    value: 'employment_status',
    label: 'Employment Status'
  }];
  const operators = [{
    value: '=',
    label: 'Equals (=)'
  }, {
    value: '>',
    label: 'Greater than (>)'
  }, {
    value: '<',
    label: 'Less than (<)'
  }, {
    value: '>=',
    label: 'Greater than or equal (>=)'
  }, {
    value: '<=',
    label: 'Less than or equal (<=)'
  }, {
    value: 'BETWEEN',
    label: 'Between'
  }, {
    value: 'IN',
    label: 'In (IN)'
  }, {
    value: 'NOT IN',
    label: 'Not In (NOT IN)'
  }, {
    value: '≠',
    label: 'Not (≠)'
  }];
  const ruleOperators = [{
    value: 'AND',
    label: 'AND'
  }, {
    value: 'OR',
    label: 'OR'
  }];
  const logicalOperators = [{
    value: 'AND',
    label: 'AND'
  }, {
    value: 'OR',
    label: 'OR'
  }];
  useEffect(() => {
    calculateTotalWeightage();
  }, [scoringGroups]);
  const calculateTotalWeightage = () => {
    const total = scoringGroups.reduce((sum, group) => sum + (group.weightage || 0), 0);
    setTotalWeightage(total);
  };
  const calculateTotalPoints = (weightage: number) => {
    return totalScoreRange * weightage / 100;
  };
  const addScoringGroup = () => {
    const newGroup: ScoringGroup = {
      id: Date.now().toString(),
      groupName: '',
      weightage: 0,
      totalPoints: 0,
      conditions: []
    };
    setScoringGroups([...scoringGroups, newGroup]);
  };
  const removeScoringGroup = (groupId: string) => {
    setScoringGroups(scoringGroups.filter(g => g.id !== groupId));
  };
  const updateScoringGroup = (groupId: string, field: keyof ScoringGroup, value: any) => {
    setScoringGroups(scoringGroups.map(g => {
      if (g.id === groupId) {
        const updated = {
          ...g,
          [field]: value
        };
        if (field === 'weightage') {
          updated.totalPoints = calculateTotalPoints(value);
        }
        return updated;
      }
      return g;
    }));
  };
  const addCondition = (groupId: string) => {
    const newCondition: Condition = {
      id: Date.now().toString(),
      rules: [{
        id: Date.now().toString(),
        factor: '',
        operator: '',
        value: ''
      }],
      points: 0,
      ruleOperator: 'AND'
    };
    setScoringGroups(scoringGroups.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          conditions: [...group.conditions, newCondition]
        };
      }
      return group;
    }));
  };
  const removeCondition = (groupId: string, conditionId: string) => {
    setScoringGroups(scoringGroups.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          conditions: group.conditions.filter(c => c.id !== conditionId)
        };
      }
      return group;
    }));
  };
  const updateCondition = (groupId: string, conditionId: string, field: keyof Condition, value: any) => {
    setScoringGroups(scoringGroups.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          conditions: group.conditions.map(c => c.id === conditionId ? {
            ...c,
            [field]: value
          } : c)
        };
      }
      return group;
    }));
  };
  const addRule = (groupId: string, conditionId: string) => {
    const newRule: Rule = {
      id: Date.now().toString(),
      factor: '',
      operator: '',
      value: ''
    };
    setScoringGroups(scoringGroups.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          conditions: group.conditions.map(condition => condition.id === conditionId ? {
            ...condition,
            rules: [...condition.rules, newRule]
          } : condition)
        };
      }
      return group;
    }));
  };
  const removeRule = (groupId: string, conditionId: string, ruleId: string) => {
    setScoringGroups(scoringGroups.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          conditions: group.conditions.map(condition => condition.id === conditionId ? {
            ...condition,
            rules: condition.rules.filter(r => r.id !== ruleId)
          } : condition)
        };
      }
      return group;
    }));
  };
  const updateRule = (groupId: string, conditionId: string, ruleId: string, field: keyof Rule, value: any) => {
    setScoringGroups(scoringGroups.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          conditions: group.conditions.map(condition => condition.id === conditionId ? {
            ...condition,
            rules: condition.rules.map(rule => rule.id === ruleId ? {
              ...rule,
              [field]: value
            } : rule)
          } : condition)
        };
      }
      return group;
    }));
  };
  const getWeightageStatus = () => {
    if (totalWeightage === 100) return 'valid';
    if (totalWeightage >= 80 && totalWeightage < 100) return 'warning';
    return 'error';
  };
  const getWeightageColor = () => {
    const status = getWeightageStatus();
    if (status === 'valid') return 'text-green-600 bg-green-50 border-green-200';
    if (status === 'warning') return 'text-amber-600 bg-amber-50 border-amber-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };
  const validateConditionScores = (group: ScoringGroup): string[] => {
    const errors: string[] = [];
    const scores = group.conditions.map(c => c.points);
    // Check if any score exceeds total points
    scores.forEach((score, index) => {
      if (score > group.totalPoints) {
        errors.push(`${group.groupName || 'Unnamed group'} - Condition ${index + 1}: Points ${score} exceeds maximum total points ${group.totalPoints.toFixed(1)}`);
      }
    });
    return errors;
  };
  const isValidForm = () => {
    if (!scoreName) return false;
    if (totalWeightage !== 100) return false;
    if (scoringGroups.length === 0) return false;
    for (const group of scoringGroups) {
      if (!group.groupName || group.weightage <= 0) return false;
      if (group.conditions.length === 0) return false;
      for (const condition of group.conditions) {
        if (condition.rules.length === 0) return false;
        for (const rule of condition.rules) {
          if (!rule.factor || !rule.operator || !rule.value) return false;
          if (rule.operator === 'BETWEEN' && !rule.value2) return false;
        }
      }
      const errors = validateConditionScores(group);
      if (errors.length > 0) return false;
    }
    return true;
  };
  const getAllValidationErrors = (): string[] => {
    const errors: string[] = [];
    if (!scoreName) errors.push('Credit Score Name is required');
    if (totalWeightage !== 100) errors.push('Total weightage must be exactly 100%');
    if (scoringGroups.length === 0) errors.push('At least one scoring group is required');
    scoringGroups.forEach(group => {
      if (!group.groupName) errors.push('All scoring groups must have a name');
      if (group.conditions.length === 0) {
        errors.push(`${group.groupName || 'Unnamed group'}: At least one condition is required`);
      }
      errors.push(...validateConditionScores(group));
    });
    return errors;
  };
  const handleSave = () => {
    if (isValidForm()) {
      console.log('Saving credit score:', {
        scoreId,
        scoreName,
        scoreDescription,
        totalScoreRange,
        scoringGroups
      });
      onSave();
    }
  };
  const handleReset = () => {
    setScoringGroups([]);
    setTotalWeightage(0);
  };
  const renderValueInput = (group: ScoringGroup, condition: Condition, rule: Rule) => {
    if (rule.operator === 'BETWEEN') {
      return <div className="flex gap-2 items-center" data-id="element-53">
          <Input type="number" placeholder="Min" value={rule.value} onChange={e => updateRule(group.id, condition.id, rule.id, 'value', e.target.value)} data-id="element-54" />
          <span className="text-gray-500" data-id="element-55">to</span>
          <Input type="number" placeholder="Max" value={rule.value2 || ''} onChange={e => updateRule(group.id, condition.id, rule.id, 'value2', e.target.value)} data-id="element-56" />
        </div>;
    } else if (rule.operator === 'IN' || rule.operator === 'NOT IN') {
      return <Input placeholder="e.g., Karachi, Lahore, Islamabad" value={rule.value} onChange={e => updateRule(group.id, condition.id, rule.id, 'value', e.target.value)} data-id="element-57" />;
    } else {
      return <Input placeholder="Enter value" value={rule.value} onChange={e => updateRule(group.id, condition.id, rule.id, 'value', e.target.value)} data-id="element-58" />;
    }
  };
  const validationErrors = getAllValidationErrors();
  return <div className="space-y-6 animate-in fade-in duration-500" data-id="element-59">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-60">
        <div className="flex items-center gap-4" data-id="element-61">
          <Button variant="ghost" onClick={onBack} data-id="element-62">
            <ArrowLeft className="h-4 w-4 mr-2" data-id="element-63" />
            Back to List
          </Button>
          <div data-id="element-64">
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight" data-id="element-65">
              {creditScore ? 'Edit Credit Score' : 'New Credit Score'}
            </h1>
            <p className="text-sm text-gray-500 mt-1" data-id="element-66">
              Configure scoring groups and rules
            </p>
          </div>
        </div>
        <div className="flex gap-3" data-id="element-67">
          <Button variant="secondary" onClick={handleReset} data-id="element-68">
            <RotateCcw className="h-4 w-4 mr-2" data-id="element-69" />
            Reset Rules
          </Button>
          <Button onClick={handleSave} disabled={!isValidForm()} data-id="element-70">
            <Save className="h-4 w-4 mr-2" data-id="element-71" />
            Save Credit Score
          </Button>
        </div>
      </div>

      {/* Header Information */}
      <div className="bg-white rounded-lg border-2 border-gray-200 shadow-sm p-6" data-id="element-72">
        <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-73">
          Credit Score Header Information
        </h2>
        <div className="grid grid-cols-2 gap-6" data-id="element-74">
          <div data-id="element-75">
            <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-76">
              Credit Score ID
              <span className="text-red-500 ml-1" data-id="element-77">*</span>
            </label>
            <Input value={scoreId} disabled data-id="element-78" />
            <p className="text-xs text-gray-500 mt-1" data-id="element-79">
              Auto-generated, immutable
            </p>
          </div>

          <div data-id="element-80">
            <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-81">
              Credit Score Name
              <span className="text-red-500 ml-1" data-id="element-82">*</span>
            </label>
            <Input value={scoreName} onChange={e => setScoreName(e.target.value)} placeholder="e.g., Standard Credit Score" data-id="element-83" />
            <p className="text-xs text-gray-500 mt-1" data-id="element-84">Must be unique</p>
          </div>

          <div className="col-span-2" data-id="element-85">
            <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-86">
              Credit Score Description
            </label>
            <Input value={scoreDescription} onChange={e => setScoreDescription(e.target.value)} placeholder="Internal notes for understanding scoring rationale" data-id="element-87" />
            <p className="text-xs text-gray-500 mt-1" data-id="element-88">Optional</p>
          </div>

          <div data-id="element-89">
            <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-90">
              Total Score Range
              <span className="text-red-500 ml-1" data-id="element-91">*</span>
            </label>
            <Select value={totalScoreRange.toString()} onChange={e => setTotalScoreRange(parseInt(e.target.value))} options={[{
            value: '100',
            label: '100'
          }, {
            value: '1000',
            label: '1000'
          }]} data-id="element-92" />
            <p className="text-xs text-gray-500 mt-1" data-id="element-93">
              Defines maximum credit score output (100 by default)
            </p>
          </div>
        </div>
      </div>

      {/* Weightage Indicator */}
      <div className={`p-4 rounded-lg border-2 ${getWeightageColor()} transition-colors`} data-id="element-94">
        <div className="flex items-center justify-between" data-id="element-95">
          <div className="flex items-center gap-3" data-id="element-96">
            {getWeightageStatus() === 'valid' ? <CheckCircle className="h-5 w-5" data-id="element-97" /> : <AlertCircle className="h-5 w-5" data-id="element-98" />}
            <div data-id="element-99">
              <div className="font-semibold" data-id="element-100">
                Total Weightage: {totalWeightage}%
              </div>
              {totalWeightage !== 100 && <div className="text-sm mt-1" data-id="element-101">
                  {totalWeightage < 100 ? `Add ${100 - totalWeightage}% more to reach 100%` : `Reduce by ${totalWeightage - 100}% to reach 100%`}
                </div>}
            </div>
          </div>
          <div className="text-right" data-id="element-102">
            <div className="text-2xl font-bold" data-id="element-103">{totalWeightage}%</div>
            <div className="text-xs" data-id="element-104">of 100%</div>
          </div>
        </div>
      </div>

      {/* Scoring Groups */}
      <div className="space-y-6" data-id="element-105">
        {scoringGroups.map((group, groupIndex) => <div key={group.id} className="bg-white rounded-lg border-2 border-gray-200 shadow-sm" data-id="element-106">
            {/* Group Header */}
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200" data-id="element-107">
              <div className="grid grid-cols-12 gap-4 items-center" data-id="element-108">
                <div className="col-span-4" data-id="element-109">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-110">
                    Field Group Name
                  </label>
                  <Input value={group.groupName} onChange={e => updateScoringGroup(group.id, 'groupName', e.target.value)} placeholder="e.g., Demographics, Financial Profile" data-id="element-111" />
                </div>

                <div className="col-span-2" data-id="element-112">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-113">
                    Weightage (%)
                  </label>
                  <Input type="number" value={group.weightage} onChange={e => updateScoringGroup(group.id, 'weightage', parseInt(e.target.value) || 0)} placeholder="0" data-id="element-114" />
                </div>

                <div className="col-span-2" data-id="element-115">
                  <div className="flex items-center gap-1 mb-1.5" data-id="element-116">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-117">
                      Total Points
                    </label>
                    <div className="relative" data-id="element-118">
                      <button onMouseEnter={() => setShowTooltip(group.id)} onMouseLeave={() => setShowTooltip(null)} className="text-gray-400 hover:text-blue-600 transition-colors" data-id="element-119">
                        <Info className="h-4 w-4" data-id="element-120" />
                      </button>
                      {showTooltip === group.id && <div className="absolute left-0 top-6 z-50 w-72 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-xl" data-id="element-121">
                          <p className="leading-relaxed" data-id="element-122">
                            Maximum points available for this group. Each
                            condition can be awarded points up to this total.
                            Conditions are evaluated in order (IF/ELSE IF
                            logic).
                          </p>
                          <div className="absolute -top-1 left-4 w-2 h-2 bg-gray-900 transform rotate-45" data-id="element-123"></div>
                        </div>}
                    </div>
                  </div>
                  <Input value={group.totalPoints.toFixed(1)} disabled data-id="element-124" />
                </div>

                <div className="col-span-3 flex items-end justify-end gap-2" data-id="element-125">
                  <Button size="sm" variant="secondary" onClick={() => addCondition(group.id)} data-id="element-126">
                    <Plus className="h-4 w-4 mr-1" data-id="element-127" />
                    Add Condition
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => removeScoringGroup(group.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-id="element-128">
                    <X className="h-4 w-4" data-id="element-129" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Conditions */}
            <div className="p-4 space-y-4" data-id="element-130">
              {group.conditions.length === 0 ? <div className="text-center py-8 text-gray-500" data-id="element-131">
                  No conditions added. Click "Add Condition" to start.
                </div> : group.conditions.map((condition, condIndex) => <div key={condition.id} className="space-y-3" data-id="element-132">
                    {condIndex > 0 && <div className="flex items-center gap-2" data-id="element-133">
                        <div className="h-px bg-gray-200 flex-1" data-id="element-134" />
                        <span className="text-sm font-semibold text-gray-700 bg-gray-100 px-3 py-1 rounded" data-id="element-135">
                          ELSE
                        </span>
                        <div className="h-px bg-gray-200 flex-1" data-id="element-136" />
                      </div>}

                    <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-200 relative" data-id="element-137">
                      {/* IF/ELSE IF Label */}
                      <div className="absolute -top-3 left-4 bg-blue-600 text-white px-3 py-0.5 rounded text-xs font-semibold" data-id="element-138">
                        {condIndex === 0 ? 'IF' : 'ELSE IF'}
                      </div>

                      {/* Rules */}
                      <div className="space-y-3 mt-2" data-id="element-139">
                        {condition.rules.map((rule, ruleIndex) => <div key={rule.id} data-id="element-140">
                            {ruleIndex > 0 && <div className="flex items-center gap-2 mb-3" data-id="element-141">
                                <div className="h-px bg-blue-300 flex-1" data-id="element-142" />
                                <Select value={condition.ruleOperator} onChange={e => updateCondition(group.id, condition.id, 'ruleOperator', e.target.value as 'AND' | 'OR')} options={ruleOperators} data-id="element-143" />
                                <div className="h-px bg-blue-300 flex-1" data-id="element-144" />
                              </div>}

                            <div className="bg-white p-3 rounded-lg border border-blue-200" data-id="element-145">
                              <div className="grid grid-cols-12 gap-3" data-id="element-146">
                                {/* Scoring Factor */}
                                <div className="col-span-3" data-id="element-147">
                                  <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-148">
                                    Scoring Factor
                                  </label>
                                  <Select value={rule.factor} onChange={e => updateRule(group.id, condition.id, rule.id, 'factor', e.target.value)} options={scoringFactorOptions} data-id="element-149" />
                                </div>

                                {/* Operator */}
                                <div className="col-span-2" data-id="element-150">
                                  <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-151">
                                    Operator
                                  </label>
                                  <Select value={rule.operator} onChange={e => updateRule(group.id, condition.id, rule.id, 'operator', e.target.value)} options={operators} data-id="element-152" />
                                </div>

                                {/* Value */}
                                <div className="col-span-6" data-id="element-153">
                                  <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-154">
                                    Value
                                  </label>
                                  {renderValueInput(group, condition, rule)}
                                </div>

                                {/* Remove Rule */}
                                <div className="col-span-1 flex items-end justify-end" data-id="element-155">
                                  {condition.rules.length > 1 && <Button variant="ghost" size="sm" onClick={() => removeRule(group.id, condition.id, rule.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-id="element-156">
                                      <X className="h-4 w-4" data-id="element-157" />
                                    </Button>}
                                </div>
                              </div>
                            </div>
                          </div>)}
                      </div>

                      {/* Add Rule and Points Row */}
                      <div className="grid grid-cols-12 gap-3 mt-3 pt-3 border-t border-blue-200" data-id="element-158">
                        <div className="col-span-3" data-id="element-159">
                          <Button size="sm" variant="secondary" onClick={() => addRule(group.id, condition.id)} className="w-full" data-id="element-160">
                            <Plus className="h-3 w-3 mr-1" data-id="element-161" />
                            Add Rule
                          </Button>
                        </div>

                        <div className="col-span-6" data-id="element-162"></div>

                        <div className="col-span-2" data-id="element-163">
                          <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-164">
                            Points
                          </label>
                          <Input type="number" value={condition.points} onChange={e => updateCondition(group.id, condition.id, 'points', parseFloat(e.target.value) || 0)} placeholder="0" max={group.totalPoints} data-id="element-165" />
                        </div>

                        {/* Remove Condition */}
                        <div className="col-span-1 flex items-end justify-end" data-id="element-166">
                          <Button variant="ghost" size="sm" onClick={() => removeCondition(group.id, condition.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-id="element-167">
                            <X className="h-4 w-4" data-id="element-168" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>)}
            </div>
          </div>)}
      </div>

      {/* Add Scoring Group Button */}
      <Button variant="secondary" onClick={addScoringGroup} className="w-full" data-id="element-169">
        <Plus className="h-4 w-4 mr-2" data-id="element-170" />
        Add Field Group
      </Button>

      {/* Validation Errors */}
      {validationErrors.length > 0 && <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4" data-id="element-171">
          <div className="flex items-start gap-2" data-id="element-172">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" data-id="element-173" />
            <div className="text-sm text-red-700" data-id="element-174">
              <div className="font-semibold mb-2" data-id="element-175">Validation Errors:</div>
              <ul className="list-disc list-inside space-y-1" data-id="element-176">
                {validationErrors.map((error, index) => <li key={index} data-id="element-177">{error}</li>)}
              </ul>
            </div>
          </div>
        </div>}
    </div>;
}